import type { AnalysisState } from '@aibi/contracts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  Kbd,
  SimpleTooltip,
} from '@aibi/ui';
import {
  ArrowUpFromLine,
  Copy,
  Ellipsis,
  Eraser,
  Maximize2,
  Minimize2,
  RectangleHorizontal,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import { useServices } from '../../../app/services';
import {
  CARD_HEIGHTS,
  CARD_WIDTHS,
  cardHeight,
  cardWidth,
  type CardHeight,
  type CardWidth,
} from '../../model/workspace-document';
import { useUiDispatch, useUiState } from '../../state/ui-state';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';

const WIDTH_LABELS: Readonly<Record<CardWidth, string>> = {
  4: 'Pequena (1/3)',
  6: 'Mediana (1/2)',
  8: 'Grande (2/3)',
  12: 'Ancho completo',
};

const HEIGHT_LABELS: Readonly<Record<CardHeight, string>> = {
  1: 'Compacta',
  2: 'Baja',
  4: 'Media',
  6: 'Alta',
  8: 'Muy alta',
};

/** Barra contextual de una tarjeta: tipo, duplicar, maximizar y menu de acciones. */
export function VisualizationToolbar({
  card,
  title,
}: {
  readonly card: AnalysisState;
  readonly title: string;
}) {
  const { index, catalog } = useWorkspace();
  const fields = [
    ...card.dimensions.map((d) => ({
      key: d.key,
      name: catalog.get(d.dimensionId)?.name ?? d.key,
    })),
    ...card.measures.map((m) => ({ key: m.key, name: catalog.get(m.measureId)?.name ?? m.key })),
  ];
  const { execute } = useWorkspaceActions();
  const uiDispatch = useUiDispatch();
  const { maximizedCardId } = useUiState();
  const { rendererRegistry } = useServices();
  const maximized = maximizedCardId === card.id;
  const available = rendererRegistry.list().filter((definition) => definition.available);
  const current = rendererRegistry.resolve(card.visualization.type);
  const CurrentIcon = current.icon;
  const drilled = card.drilldown.filter((drill) => drill.path.length > 0);

  return (
    <div role="toolbar" aria-label={`Acciones de ${title}`} className="flex items-center gap-0.5">
      <DropdownMenu>
        <SimpleTooltip content={`Tipo: ${current.label}`}>
          <DropdownMenuTrigger asChild>
            <IconButton label={`Cambiar tipo (actual: ${current.label})`}>
              <CurrentIcon />
            </IconButton>
          </DropdownMenuTrigger>
        </SimpleTooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Tipo de visualizacion</DropdownMenuLabel>
          {available.map((definition) => {
            const Icon = definition.icon;
            return (
              <DropdownMenuItem
                key={definition.type}
                onSelect={() => {
                  execute({
                    type: 'set-visualization-type',
                    cardId: card.id,
                    visualization: definition.type,
                  });
                }}
              >
                <Icon /> {definition.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {drilled.map((drill) => (
        <SimpleTooltip key={drill.hierarchyId} content="Subir un nivel en la jerarquia">
          <IconButton
            label={`Subir nivel en ${index.getHierarchy(drill.hierarchyId)?.name ?? drill.hierarchyId}`}
            onClick={() => {
              execute({ type: 'drill-up', cardId: card.id, hierarchyId: drill.hierarchyId });
            }}
          >
            <ArrowUpFromLine />
          </IconButton>
        </SimpleTooltip>
      ))}
      <SimpleTooltip
        content={
          <span className="inline-flex items-center gap-1.5">
            Duplicar <Kbd>Ctrl</Kbd>
            <Kbd>D</Kbd>
            <span className="text-muted-foreground">·</span>
            <Kbd>Ctrl</Kbd>
            <Kbd>C</Kbd>
            <Kbd>V</Kbd>
          </span>
        }
      >
        <IconButton
          label="Duplicar visualizacion"
          onClick={() => {
            execute({ type: 'duplicate-card', cardId: card.id });
          }}
        >
          <Copy />
        </IconButton>
      </SimpleTooltip>
      <SimpleTooltip
        content={
          <span className="inline-flex items-center gap-1.5">
            {maximized ? 'Restaurar' : 'Maximizar'} <Kbd>Ctrl</Kbd>
            <Kbd>M</Kbd>
          </span>
        }
      >
        <IconButton
          label={maximized ? 'Restaurar tamano' : 'Maximizar visualizacion'}
          active={maximized}
          onClick={() => {
            uiDispatch({ type: 'toggle-maximize', cardId: card.id });
          }}
        >
          {maximized ? <Minimize2 /> : <Maximize2 />}
        </IconButton>
      </SimpleTooltip>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton label="Mas acciones">
            <Ellipsis />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Ancho</DropdownMenuLabel>
          {CARD_WIDTHS.map((width) => (
            <DropdownMenuItem
              key={width}
              onSelect={() => {
                execute({ type: 'resize-card', cardId: card.id, width });
              }}
            >
              <RectangleHorizontal /> {WIDTH_LABELS[width]}
              {cardWidth(card) === width ? ' · actual' : ''}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Alto</DropdownMenuLabel>
          {CARD_HEIGHTS.map((height) => (
            <DropdownMenuItem
              key={height}
              onSelect={() => {
                execute({ type: 'resize-card', cardId: card.id, height });
              }}
            >
              <Square /> {HEIGHT_LABELS[height]}
              {cardHeight(card) === height ? ' · actual' : ''}
            </DropdownMenuItem>
          ))}
          {fields.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Quitar campo</DropdownMenuLabel>
              {fields.map((field) => (
                <DropdownMenuItem
                  key={field.key}
                  aria-label={`Quitar ${field.name}`}
                  onSelect={() => {
                    execute({ type: 'remove-field', cardId: card.id, key: field.key });
                  }}
                >
                  <X /> {field.name}
                </DropdownMenuItem>
              ))}
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={card.selections.length === 0}
            onSelect={() => {
              execute({ type: 'clear-selections' });
            }}
          >
            <Eraser /> Limpiar selecciones
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive"
            onSelect={() => {
              execute({ type: 'remove-card', cardId: card.id });
            }}
          >
            <Trash2 /> Eliminar visualizacion
            <Kbd className="ml-auto">Supr</Kbd>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
